import { DeleteOutlined, EditOutlined, MoreOutlined, StarFilled } from "@ant-design/icons";
import { Avatar, Button, Dropdown, Empty, Popconfirm, Space, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useUrlTableState } from "@/shared/hooks/useUrlState";
import { useDeleteOrganizationUser, useOrganizationUsers } from "../hooks/useOrganizationUsers";
import { useOrgUsersStore } from "../store/org-users.store";
import type { OrganizationUser } from "../types";

interface OrgUsersTableProps {
	organizationId: string;
}

function getUserDisplay(record: OrganizationUser) {
	const raw = record as OrganizationUser & {
		fullname?: string;
		phone?: string;
	};

	const fullname = record.user?.fullname ?? raw.fullname ?? "Noma'lum";
	const phone = record.user?.phone ?? raw.phone ?? "";

	return { fullname, phone };
}

function hasEditableAssignment(record: OrganizationUser) {
	return Boolean(record.user?.id && record.position?.id);
}

export function OrgUsersTable({ organizationId }: OrgUsersTableProps) {
	const { page, limit, setPage } = useUrlTableState({ prefix: "users" });

	const { data, isLoading } = useOrganizationUsers(organizationId, {
		page,
		limit,
	});
	const { mutate: remove } = useDeleteOrganizationUser(organizationId);
	const openEditModal = useOrgUsersStore((s) => s.openEditModal);

	const columns: ColumnsType<OrganizationUser> = [
		{
			title: "№",
			key: "index",
			width: 55,
			align: "center",
			render: (_: unknown, __: unknown, index: number) => (page - 1) * limit + index + 1,
		},
		{
			title: "Xodim",
			key: "user",
			render: (_: unknown, record: OrganizationUser) => {
				const { fullname, phone } = getUserDisplay(record);
				return (
					<Space>
						<Avatar size={32} style={{ background: "#1D2026", color: "#fff", fontSize: 13 }}>
							{fullname.charAt(0)?.toUpperCase() ?? "U"}
						</Avatar>
						<div>
							<div className="font-medium text-sm">{fullname}</div>
							<div className="text-xs text-gray-400">{phone}</div>
						</div>
					</Space>
				);
			},
		},
		{
			title: "Lavozim",
			key: "position",
			render: (_: unknown, record: OrganizationUser) => (
				<div>
					<div className="text-sm">{record.position?.name ?? "—"}</div>
					<div className="text-xs text-gray-400">{record.position?.type?.name ?? ""}</div>
				</div>
			),
		},
		{
			title: "Turi",
			key: "isPrimary",
			width: 100,
			align: "center",
			render: (_: unknown, record: OrganizationUser) =>
				record.isPrimary ? (
					<Tooltip title="Asosiy xodim">
						<Tag color="gold" icon={<StarFilled />}>
							Asosiy
						</Tag>
					</Tooltip>
				) : (
					<Tag color="default">Oddiy</Tag>
				),
		},
		{
			title: "Qo'shilgan",
			dataIndex: "createdAt",
			key: "createdAt",
			width: 130,
			render: (date: string) =>
				new Date(date).toLocaleDateString("uz-UZ", {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
				}),
		},
		{
			title: "Amallar",
			key: "actions",
			width: 80,
			align: "center",
			render: (_: unknown, record: OrganizationUser) => (
				<Dropdown
					menu={{
						items: [
							{
								key: "edit",
								icon: <EditOutlined />,
								label: "Tahrirlash",
								disabled: !hasEditableAssignment(record),
								onClick: () => {
									if (!hasEditableAssignment(record)) {
										return;
									}
									openEditModal(record);
								},
							},
							{
								key: "delete",
								danger: true,
								label: (
									// biome-ignore lint/a11y/useKeyWithClickEvents: ignore
									// biome-ignore lint/a11y/noStaticElementInteractions: ignore
									<div onClick={(e) => e.stopPropagation()}>
										<Popconfirm
											title="O'chirish"
											description="Bu xodimni tashkilotdan olib tashlaysizmi?"
											onConfirm={() => remove(record.id)}
											okText="Ha"
											cancelText="Yo'q"
											okButtonProps={{ danger: true }}
										>
											<div
												style={{
													width: "100%",
													display: "flex",
													alignItems: "center",
													gap: "8px",
												}}
											>
												<DeleteOutlined />
												<span>O'chirish</span>
											</div>
										</Popconfirm>
									</div>
								),
							},
						],
					}}
					trigger={["click"]}
					placement="bottomRight"
				>
					<Button type="text" icon={<MoreOutlined />} size="small" />
				</Dropdown>
			),
		},
	];

	return (
		<Table
			columns={columns}
			dataSource={data?.data?.items}
			loading={isLoading}
			rowKey="id"
			pagination={{
				current: page,
				pageSize: limit,
				total: data?.data?.meta?.total,
				onChange: setPage,
				showSizeChanger: false,
				showTotal: (total) => `Jami: ${total} ta xodim`,
			}}
			size="middle"
			locale={{
				emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Ma'lumot yo'q" />,
			}}
		/>
	);
}
